import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../App';
import { Building2, Plus, ArrowLeft, Trash2, Mail, CheckCircle, Clock, XCircle, Search } from 'lucide-react';
import { useTheme } from '../../components/ui/theme-provider';
import { cn } from '../../lib/utils';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { practiceDataUtil } from '../../helpers/practiceDataUtil';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { ScrollArea } from '../../components/ui/scroll-area';
import { geocodePostcode } from '../../utils/geolocationService'; // Add this import

const ManagePracticesPage = ({ user, currentUser }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [managedPractices, setManagedPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Practice selection state
  const [showPracticeSelector, setShowPracticeSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Use practiceDataUtil directly instead of context
  const [allPractices, setAllPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);

  // Load practice data on component mount
  useEffect(() => {
    const loadPractices = async () => {
      try {
        setPracticesLoading(true);
        const practices = await practiceDataUtil.getAllPractices();
        setAllPractices(practices || []);
      } catch (err) {
        console.error('Error loading practices:', err);
        setError('Failed to load practice data. Please try again.');
      } finally {
        setPracticesLoading(false);
      }
    };

    loadPractices();
  }, []);

  // Load existing practices from currentUser
  useEffect(() => {
    if (currentUser?.managedPractices) {
      setManagedPractices([...currentUser.managedPractices]);
    }
  }, [currentUser]);

  const generatePracticeId = () => {
    return 'practice_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  // Use the same search logic as PracticeSettings.jsx
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);

    // Debounced search with same logic as PracticeSettings.jsx
    setTimeout(() => {
      const normalizedSearchTerm = term.toLowerCase().replace(/\s+/g, ' ').split(' ');
      
      const results = (allPractices || []).filter(practice => {
        const searchableText = [
          (practice.prac_name || '').toLowerCase(),
          (practice.address1 || '').toLowerCase(),
          (practice.address2 || '').toLowerCase(),
          (practice.address3 || '').toLowerCase(),
          (practice.address4 || '').toLowerCase(),
          (practice.address5 || '').toLowerCase(),
          (practice.postcode || '').replace(/\s+/g, '').toLowerCase()
        ].join(' ');
        
        return normalizedSearchTerm.every(searchTerm => searchableText.includes(searchTerm));
      });

      setSearchResults(results);
      setSearching(false);
    }, 300);
  };

  const addPracticeFromSelection = (selectedPractice) => {
    const isAlreadyAdded = managedPractices.some(p => p.prac_code === selectedPractice.prac_code);
    if (isAlreadyAdded) {
      setError('This practice is already in your list');
      return;
    }

    const newPractice = {
      id: generatePracticeId(),
      prac_code: selectedPractice.prac_code,
      practiceName: selectedPractice.prac_name,
      practicePostcode: selectedPractice.postcode,
      practiceAddress: `${selectedPractice.address1}${selectedPractice.address4 ? ', ' + selectedPractice.address4 : ''}`,
      practiceOfficialEmail: '',
      verificationStatus: 'not_submitted',
      submittedAt: null,
      reviewedAt: null,
      reviewedBy: null,
      rejectionReason: '',
      adminNotes: ''
    };

    setManagedPractices(prev => [...prev, newPractice]);
    setShowPracticeSelector(false);
    setSearchTerm('');
    setSearchResults([]);
    setError('');
  };

  const updatePracticeEmail = (practiceId, email) => {
    setManagedPractices(prev => 
      prev.map(practice => 
        practice.id === practiceId 
          ? { ...practice, practiceOfficialEmail: email }
          : practice
      )
    );
    
    if (error) setError('');
  };

  const removePractice = (practiceId) => {
    setManagedPractices(prev => prev.filter(practice => practice.id !== practiceId));
  };

  const canSubmitForVerification = (practice) => {
    return practice.verificationStatus === 'not_submitted' || practice.verificationStatus === 'rejected';
  };

  const submitForVerification = async (practiceId) => {
    const practice = managedPractices.find(p => p.id === practiceId);
    
    if (!practice.practiceOfficialEmail.trim()) {
      setError('Official practice email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(practice.practiceOfficialEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const updatedPractices = managedPractices.map(p => 
        p.id === practiceId 
          ? { 
              ...p, 
              verificationStatus: 'pending',
              submittedAt: new Date(),
              rejectionReason: '',
            }
          : p
      );

      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        managedPractices: updatedPractices,
        lastUpdated: serverTimestamp()
      });

      setManagedPractices(updatedPractices);
      setSuccess(`${practice.practiceName} has been submitted for verification`);
    } catch (err) {
      console.error('Error submitting for verification:', err);
      setError('Failed to submit for verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveAllPractices = async () => {
    const invalidPractices = managedPractices.filter(p => 
      !p.practiceOfficialEmail.trim()
    );

    if (invalidPractices.length > 0) {
      setError('Please provide official email addresses for all practices before saving');
      return;
    }

    const invalidEmails = managedPractices.filter(p => 
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.practiceOfficialEmail)
    );

    if (invalidEmails.length > 0) {
      setError('Please enter valid email addresses for all practices');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        managedPractices: managedPractices,
        lastUpdated: serverTimestamp()
      });

      setSuccess('All practices saved successfully');
    } catch (err) {
      console.error('Error saving practices:', err);
      setError('Failed to save practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Building2 className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'rejected':
        return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      default:
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  if (practicesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-muted-foreground mt-4">Loading practice data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Page Header */}
      <div className="flex h-[60px] items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.PRACTICE_DASHBOARD)}
            className={cn(
              "flex items-center gap-2",
              theme === 'light' ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className={cn(
              "text-3xl font-bold tracking-tight",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              Manage & Verify Practices
            </h1>
            <p className={cn(
              "text-sm mt-1",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              Select practices you manage and submit them for verification
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowPracticeSelector(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Practice
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
          <p className="text-green-800 dark:text-green-200 text-sm">{success}</p>
        </div>
      )}

      {/* Instructions Card */}
      <Card className={cn(
        theme === 'light' ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-800'
      )}>
        <CardContent className="p-4">
          <h3 className={cn(
            "font-semibold mb-2",
            theme === 'light' ? 'text-blue-900' : 'text-blue-200'
          )}>
            Verification Process
          </h3>
          <div className={cn(
            "text-sm space-y-1",
            theme === 'light' ? 'text-blue-800' : 'text-blue-300'
          )}>
            <p>1. Select practices you manage from our database</p>
            <p>2. Provide the official email address for each practice</p>
            <p>3. Submit each practice for verification individually</p>
            <p>4. Our admin team will verify your association with each practice</p>
            <p>5. Once verified, you can post job listings for that practice</p>
          </div>
          <div className={cn(
            "mt-3 p-2 rounded border text-xs",
            theme === 'light' ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-blue-900/30 border-blue-700 text-blue-200'
          )}>
            <strong>Can't find your practice or notice incorrect details?</strong> Please email{' '}
            <a 
              href="mailto:support@coooly.co.uk?subject=Practice%20Data%20Issue" 
              className="underline font-medium hover:opacity-80"
            >
              support@coooly.co.uk
            </a>{' '}
            and we'll help update the information.
          </div>
        </CardContent>
      </Card>

      {/* Practice Selector Modal */}
      {showPracticeSelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className={cn(
            "w-full max-w-2xl h-[80vh] flex flex-col",
            theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E]'
          )}>
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={cn(
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  )}>
                    Select Practice
                  </CardTitle>
                  <CardDescription className={cn(
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    Search for the practice you manage
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowPracticeSelector(false);
                    setSearchTerm('');
                    setSearchResults([]);
                  }}
                >
                  ×
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col space-y-4 min-h-0">
              {/* Search Input */}
              <div className="relative flex-shrink-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by practice name, address, town, or postcode..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={cn(
                    "pl-10",
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#1F1F2E] border-gray-700'
                  )}
                  style={theme === 'light' ? { 
                    backgroundColor: 'white',
                    '--hover-bg': 'white',
                    '--focus-bg': 'white'
                  } : {}}
                />
                {/* Override hover styles specifically for light theme */}
                {theme === 'light' && (
                  <style>{`
                    .relative input:hover {
                      background-color: white !important;
                    }
                    .relative input:focus {
                      background-color: white !important;
                    }
                  `}</style>
                )}
              </div>

              {/* Search Results */}
              <div className={cn(
                "border rounded-md flex-1 overflow-hidden",
                theme === 'light' ? 'border-gray-200' : 'border-gray-700'
              )}>
                {searching ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <LoadingSpinner size="small" />
                      <p className="text-sm text-muted-foreground mt-2">Searching...</p>
                    </div>
                  </div>
                ) : searchResults.length > 0 ? (
                  <ScrollArea className="h-full">
                    <div className="p-2 space-y-2">
                      {searchResults.slice(0, 10).map(practice => (
                        <div
                          key={practice.prac_code}
                          onClick={() => addPracticeFromSelection(practice)}
                          className={cn(
                            "p-3 rounded-md cursor-pointer transition-colors",
                            theme === 'light' 
                              ? 'hover:bg-gray-100 border border-gray-200' 
                              : 'hover:bg-gray-700/50 border border-gray-700'
                          )}
                        >
                          <div className={cn(
                            "font-medium",
                            theme === 'light' ? 'text-gray-900' : 'text-white'
                          )}>
                            {practice.prac_name}
                          </div>
                          <div className={cn(
                            "text-sm",
                            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                          )}>
                            {practice.address1}
                            {practice.address4 && `, ${practice.address4}`}
                            {practice.postcode && `, ${practice.postcode}`}
                          </div>
                        </div>
                      ))}
                      {searchResults.length > 10 && (
                        <div className="p-3 text-center text-sm text-muted-foreground border-t">
                          Showing 10 of {searchResults.length} results. Try refining your search for more specific results.
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                ) : searchTerm.length >= 3 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-muted-foreground">
                      <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No practices found matching your search</p>
                      <p className="text-xs mt-1">Try different keywords or check the spelling</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-muted-foreground">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Type at least 3 characters to search for practices</p>
                      <p className="text-xs mt-1">Search by name, address, town, or postcode</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Practices List */}
      {managedPractices.length === 0 ? (
        <Card className={cn(
          theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
        )}>
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className={cn(
              "text-lg font-medium mb-2",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              No practices added yet
            </h3>
            <p className={cn(
              "text-sm mb-4",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              Start by selecting the practices you manage from our database
            </p>
            <Button
              onClick={() => setShowPracticeSelector(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Select Your First Practice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {managedPractices.map((practice) => (
            <Card key={practice.id} className={cn(
              theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
            )}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(practice.verificationStatus)}
                    <div>
                      <CardTitle className={cn(
                        "text-lg",
                        theme === 'light' ? 'text-gray-900' : 'text-white'
                      )}>
                        {practice.practiceName}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-full font-medium",
                          getStatusColor(practice.verificationStatus)
                        )}>
                          {practice.verificationStatus.replace('_', ' ').toUpperCase()}
                        </span>
                        {practice.verificationStatus === 'pending' && practice.submittedAt && (
                          <span className="text-xs text-muted-foreground">
                            Submitted {new Date(practice.submittedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removePractice(practice.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Practice Details (Read-only) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={cn(
                      "text-sm font-medium",
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    )}>
                      Practice Address
                    </Label>
                    <div className={cn(
                      "p-2 rounded border text-sm",
                      theme === 'light' ? 'bg-gray-50 border-gray-200 text-gray-700' : 'bg-[#1F1F2E] border-gray-700 text-gray-300'
                    )}>
                      {practice.practiceAddress}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className={cn(
                      "text-sm font-medium",
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    )}>
                      Postcode
                    </Label>
                    <div className={cn(
                      "p-2 rounded border text-sm",
                      theme === 'light' ? 'bg-gray-50 border-gray-200 text-gray-700' : 'bg-[#1F1F2E] border-gray-700 text-gray-300'
                    )}>
                      {practice.practicePostcode}
                    </div>
                  </div>
                </div>

                {/* Official Email (Editable) */}
                <div className="space-y-2">
                  <Label htmlFor={`email-${practice.id}`} className={cn(
                    "text-sm font-medium",
                    theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                  )}>
                    <Mail className="h-4 w-4 inline mr-1" />
                    Official Practice Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`email-${practice.id}`}
                    type="email"
                    value={practice.practiceOfficialEmail}
                    onChange={(e) => updatePracticeEmail(practice.id, e.target.value)}
                    placeholder="e.g., contact@brightsmiles.co.uk"
                    className={cn(
                      theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#1F1F2E] border-gray-700'
                    )}
                    style={theme === 'light' ? { backgroundColor: 'white !important' } : {}}
                    disabled={practice.verificationStatus === 'verified'}
                  />
                  <p className={cn(
                    "text-xs",
                    theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                  )}>
                    This email will be used by our admin team to verify your association with the practice
                  </p>
                </div>

                {/* Rejection Reason */}
                {practice.verificationStatus === 'rejected' && practice.rejectionReason && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                      Verification Rejected
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {practice.rejectionReason}
                    </p>
                  </div>
                )}

                {/* Action Button */}
                {canSubmitForVerification(practice) && (
                  <Button
                    onClick={() => submitForVerification(practice.id)}
                    disabled={loading || !practice.practiceOfficialEmail.trim()}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {loading ? 'Submitting...' : 'Submit for Verification'}
                  </Button>
                )}

                {practice.verificationStatus === 'verified' && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      ✓ This practice is verified and ready for job postings
                    </p>
                  </div>
                )}

                {practice.verificationStatus === 'pending' && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      ⏳ Verification in progress - We'll notify you once reviewed
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Save All Button */}
          {managedPractices.length > 0 && (
            <div className="flex justify-center">
              <Button
                onClick={saveAllPractices}
                disabled={loading}
                variant="outline"
                className={cn(
                  theme === 'light' ? 'text-purple-700 border-purple-300 hover:bg-purple-100' : 'text-purple-300 border-purple-500/50 hover:bg-purple-900/30'
                )}
              >
                Save All Changes
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagePracticesPage;
